import { useTranslation } from "react-i18next"

import { toAppLocale } from "@/lib/i18n/locales"
import { cn } from "@/lib/utils"
import type { CollabTimelineStep } from "@/types/crew"

/**
 * OKS-1038 — a collab's dated steps (UX Spec §2 Timeline), oldest first.
 *
 * Server-built (`Collabs::Timeline`): the FE neither sorts nor filters. The
 * last `kind: "state"` step's date IS the row's « Mis à jour » (PRD R5), so the
 * two cannot disagree — this component just renders what it is given.
 *
 * State steps reuse the collab state-pill labels; side facts get their own
 * muted secondary dots.
 */
export function CrewMemberCollabTimeline({
  steps,
  testId,
}: {
  steps: CollabTimelineStep[]
  testId: string
}) {
  const { t, i18n } = useTranslation()
  const locale = toAppLocale(i18n.language)
  const dateFormat = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
  const timeFormat = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  })

  const label = (step: CollabTimelineStep): string => {
    if (step.kind === "state") return t(`collabs.state_pill.${step.key}`)

    switch (step.key) {
      case "address_confirmed":
        return t("crew.detail.collab.timeline.address_confirmed", {
          source: provenanceLabel(t, step.source),
        })
      case "no_ship":
        return t("crew.detail.collab.timeline.no_ship")
      case "no_ship_voided":
        return t("crew.detail.collab.timeline.no_ship_voided")
      case "received":
        return t("crew.detail.collab.timeline.received")
      // OKS-1042 — TWO whole-sentence keys, no interpolation (UX-Spec UQ7).
      // `provenanceLabel` is deliberately NOT used here: it returns the
      // Shipments queue's noun phrases (« Choisi par la marque »), which would
      // read « Produit choisi — Choisi par la marque ».
      //
      // ⚠️ Falls through to the raw key on an unexpected source rather than
      // defaulting to one of them. A ternary would ASSERT a provenance instead
      // of failing, and « par le créateur » on a brand pick is the exact lie
      // this fact exists to prevent. The server only ever emits creator|brand
      // (`Collabs::Timeline::PRODUCT_CHOICE_FACT_SOURCES`), so this is
      // unreachable while that guard holds — and loud if it stops.
      case "product_chosen":
        if (step.source === "brand")
          return t("crew.detail.collab.timeline.product_chosen_brand")
        if (step.source === "creator")
          return t("crew.detail.collab.timeline.product_chosen_creator")
        return step.key
      default:
        return step.key
    }
  }

  return (
    <ol className="flex flex-col gap-2 border-l pl-4" data-testid={testId}>
      {steps.map((step) => {
        const date = new Date(step.at)
        return (
          <li
            key={`${step.kind}-${step.key}`}
            className="relative flex flex-col gap-0.5 md:flex-row md:items-baseline md:justify-between md:gap-3"
            data-kind={step.kind}
            data-step={step.key}
          >
            <span
              aria-hidden="true"
              className={cn(
                "absolute top-1.5 -left-[1.3rem] size-2 rounded-full",
                step.kind === "state"
                  ? "bg-foreground"
                  : "bg-muted-foreground/50",
              )}
            />
            <span
              className={cn(
                "text-sm",
                step.kind === "fact" && "text-muted-foreground",
              )}
            >
              {label(step)}
              {step.note && (
                <span className="text-muted-foreground line-clamp-3 block text-xs">
                  {t("crew.detail.collab.timeline.note", { note: step.note })}
                </span>
              )}
            </span>
            <time
              dateTime={step.at}
              className="text-muted-foreground text-xs whitespace-nowrap"
            >
              {`${dateFormat.format(date)} · ${timeFormat.format(date)}`}
            </time>
          </li>
        )
      })}
    </ol>
  )
}

/** The Shipments queue's provenance copy (OKS-1036/1037), text only. */
export function provenanceLabel(
  t: ReturnType<typeof useTranslation>["t"],
  source: CollabTimelineStep["source"],
): string {
  switch (source) {
    case "creator":
      return t("shipments.address.source_creator")
    case "profile_auto":
      return t("shipments.address.source_profile_auto")
    case "brand":
      return t("shipments.address.source_brand")
    default:
      return ""
  }
}
